/**
 * Options de pagination pour les requêtes
 */
export interface PaginationOptions {
  limit?: number
  page?: number
  offset?: number
  sortBy?: string[]
}

/**
 * Réponse paginée générique
 * @template T - Type des éléments contenus dans la page
 */
export interface PaginatedResponse<T> {
  content: T[]
  totalElements: number
  totalPages: number
  currentPage: number
  pageSize: number
  hasNext: boolean
  hasPrevious: boolean
}

/**
 * Classe utilitaire pour créer des réponses paginées
 */
export class PaginationHelper {
  /**
   * Crée une réponse paginée à partir des données et métadonnées
   */
  static createResponse<T>(
    content: T[],
    currentPage: number,
    pageSize: number,
    totalElements: number
  ): PaginatedResponse<T> {
    const totalPages = Math.ceil(totalElements / pageSize)

    return {
      content,
      totalElements,
      totalPages,
      currentPage,
      pageSize,
      hasNext: currentPage < totalPages - 1,
      hasPrevious: currentPage > 0
    }
  }

  /**
   * Convertit les options de pagination en paramètres de requête
   */
  static toQueryParams(options?: PaginationOptions): URLSearchParams {
    const params = new URLSearchParams()

    if (options?.limit !== undefined) {
      params.append('limit', options.limit.toString())
    }
    if (options?.page !== undefined) {
      params.append('page', options.page.toString())
    }
    if (options?.offset !== undefined) {
      params.append('offset', options.offset.toString())
    }
    if (options?.sortBy && options.sortBy.length > 0) {
      options.sortBy.forEach(sort => params.append('sortBy', sort))
    }

    return params
  }

  /**
   * Construit une URL avec les paramètres de pagination
   */
  static buildUrl(baseUrl: string, options?: PaginationOptions): string {
    const params = this.toQueryParams(options)
    const queryString = params.toString()
    return queryString ? `${baseUrl}?${queryString}` : baseUrl
  }
}